<template>
    <side-panel class="service-details">
        <div class="position-sticky title d-flex flex-row justify-content-between px-3 pt-3 pb-3">
            <div class="mr-1">
                <router-link class="btn btn-outline-dark border-0 rounded-0"
                             to="/services">
                    <font-awesome-icon icon="arrow-left"></font-awesome-icon>
                </router-link>
            </div>
            <div class="flex-grow-1 mr-1">
                <h3 class="font-weight-bolder mb-1">
                    {{ name }}
                </h3>
                <p class="text-dark mb-0">
                    新增於{{ timeCreated }}
                </p>
            </div>
            <div>
                <b-avatar v-b-tooltip.html
                          v-bind:title="owner ? owner.name : ''"
                          placement="auto"
                          variant="light"
                          size="3.75rem"
                          v-bind:src="owner && owner.avatar ? `${ $config.apiUrl }/${ $config.apiVersion }/user/avatar/${ owner.avatar }` : ''">
                </b-avatar>
            </div>
        </div>

        <div class="px-3 mb-2">
            <h6 class="mb-0">
                <color-dot size="sm"
                           v-bind:color="category.color">
                </color-dot>
                {{ category.name }}
            </h6>
        </div>

        <div class="photo-wrapper px-2 pb-2">
            <div class="img-thumbnail">
                <swiper class="bg-light"
                        ref="photoSwiper"
                        v-bind:options="swiperOptions">
                    <swiper-slide v-for="aPhoto in photo"
                                  v-bind:key="aPhoto">
                        <img class="photo w-100"
                             v-bind:alt="name"
                             v-bind:src="`${ $config.apiUrl }/${ $config.apiVersion }/service/image/${ aPhoto }`">
                    </swiper-slide>
                    <div class="swiper-pagination"
                         slot="pagination">
                    </div>
                </swiper>
            </div>
        </div>

        <service-status-bar class="mb-3"
                            v-bind:service="service">
        </service-status-bar>

        <div class="px-3 pb-3">
            <div class="mb-3"
                 v-if="description">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'info-square']"></font-awesome-icon> 服務描述
                </label>
                <p class="content">
                    {{ description }}
                </p>
            </div>

            <div class="mb-3"
                 v-if="size">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'ruler-combined']"></font-awesome-icon> 尺寸
                </label>
                <p class="content">
                    {{ size }}
                </p>
            </div>

            <div class="mb-3"
                 v-if="rebate && ['vending-admin', 'vending-site-manager'].includes(userRole)">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'money-check-edit-alt']"></font-awesome-icon> 回饋分成
                </label>
                <p class="content">
                    {{ rebate }}
                </p>
            </div>

            <div class="mb-3"
                 v-if="contractPeriod && ['vending-admin', 'vending-site-manager'].includes(userRole)">
                <label class="font-weight-bold mb-1">
                    <font-awesome-icon v-bind:icon="['fad', 'calendar-alt']"></font-awesome-icon> 合作期限
                </label>
                <p class="content">
                    {{ contractPeriod }}
                </p>
            </div>
        </div>

        <div class="position-sticky actions p-3 d-flex justify-content-between"
             v-if="['vending-admin', 'vending-vendor', ].includes(userRole)">
            <button class="btn btn-block btn-warning shadow-sm m-0 mr-1"
                    v-bind:class="{ 'btn-loading': isEditing }"
                    v-on:click="isEditServiceModalOpened = true">
                <font-awesome-icon icon="edit"></font-awesome-icon> 編輯服務
            </button>
            <button class="btn btn-block btn-danger shadow-sm m-0 ml-1"
                    v-bind:class="{ 'btn-loading': isEditing }"
                    v-on:click="isReauthenticateModalOpened = true">
                <font-awesome-icon icon="trash-alt"></font-awesome-icon> 刪除服務
            </button>
        </div>

        <modal-service is-editing
                       v-if="['vending-admin', 'vending-vendor', ].includes(userRole)"
                       v-bind:service="service"
                       v-model="isEditServiceModalOpened">
        </modal-service>

        <modal-re-authenticate v-if="['vending-admin', 'vending-vendor'].includes(userRole)"
                               v-bind:email="userEmail"
                               v-bind:callback="openDeleteServiceModal"
                               v-model="isReauthenticateModalOpened">
            <p>You are conducting a sensitive action. Please authenticate yourself. </p>
        </modal-re-authenticate>

        <modal-delete-confirm v-if="['vending-admin', 'vending-vendor'].includes(userRole)"
                              v-bind:user="service"
                              v-bind:callback="deleteService"
                              v-model="isDeleteServiceModalOpened">
        </modal-delete-confirm>
    </side-panel>
</template>

<script>
    import {
        formatDistanceToNow,
        parseISO,
    } from "date-fns";

    import {
        zhTW,
    } from "date-fns/locale";

    import {
        BAvatar,
        VBTooltip,
    } from "bootstrap-vue";

    import SwiperClass, { Navigation, Pagination } from "swiper";
    SwiperClass.use([Navigation, Pagination]);
    import getAwesomeSwiper from "vue-awesome-swiper/dist/exporter";
    const {
        Swiper,
        SwiperSlide,
    } = getAwesomeSwiper(SwiperClass);
    import "swiper/swiper-bundle.min.css";

    import SidePanel from "../SidePanel";
    import ModalService from "./ModalService";
    import ColorDot from "../ColorDot";
    import ModalReAuthenticate from "../modals/ModalReAuthenticate";
    import ModalDeleteConfirm from "../modals/ModalDeleteConfirm";
    import ServiceStatusBar from "./ServiceStatusBar";

    export default {
        name: "ServiceDetails",
        components: {
            ServiceStatusBar,
            ModalDeleteConfirm,
            ModalReAuthenticate,
            BAvatar,
            Swiper,
            SwiperSlide,
            ModalService,
            SidePanel,
            ColorDot,
        },
        directives: {
            "b-tooltip": VBTooltip,
        },
        props: {
            "serviceId": {
                type: String,
                required: true,
            },
        },
        data () {
            return {
                isEditing: false,
                isReauthenticateModalOpened: false,
                isEditServiceModalOpened: false,
                isDeleteServiceModalOpened: false,

                swiperOptions: {
                    pagination: {
                        el: ".swiper-pagination"
                    },
                },
            };
        },
        computed: {
            swiperInstance () {
                return this.$refs.photoSwiper.$swiper
            },

            userEmail () {
                return this.$store.state.user.user.email;
            },

            userRole () {
                return this.$store.state.user.user.role;
            },

            services () {
                return this.$store.state.data.services;
            },

            service () {
                const target = this.services
                    .find(
                        ({ _id }) => _id === this.serviceId
                    );
                if (target) {
                    return target;
                }
                return null;
            },

            name () {
                return this.service ? this.service.name: "";
            },

            category () {
                return this.service ? this.service.category: "";
            },

            description () {
                return this.service ? this.service.description: "";
            },

            size () {
                return this.service ? this.service.size: "";
            },

            rebate () {
                return this.service ? this.service.rebate: "";
            },

            contractPeriod () {
                return this.service ? this.service.contractPeriod: "";
            },

            owner () {
                return this.service && this.service.owner ? this.service.owner: {};
            },

            photo () {
                if (this.service) {
                    return this.service.photo;
                }
                return "";
            },

            logo () {
                if (this.service) {
                    return this.service.logo;
                }
                return "";
            },

            timeCreated () {
                if (this.service) {
                    return formatDistanceToNow(
                        parseISO(this.service.createdAt),
                        {
                            "locale": zhTW,
                            "addSuffix": true,
                        }
                    );
                }
                return "";
            },
        },
        methods: {
            openDeleteServiceModal () {
                this.isDeleteServiceModalOpened = true;
            },

            async deleteService () {
                this.isEditing = true;
                try {
                    await this.$store.dispatch(
                        "data/deleteService",
                        this.serviceId
                    );
                } catch (e) {
                    console.error(e);
                    this.isEditing = false;
                    return;
                }
                this.isEditing = false;
                this.$router.push("/services");
            },
        },
    }
</script>

<style lang="scss" scoped>
    .service-details {
        .photo-wrapper {
            .photo {
                height: 250px;
                object-fit: contain;
            }
        }

        .content {
            white-space: pre-line;
            word-break: break-word;
        }
    }
</style>
